import { useEffect, useState } from 'react'

import settings from '../../settings'
import { tr, useFormValues } from 'mmfintech-commons'
import { useProblemReporting, useSessionId } from 'mmfintech-checkout-commons'

import { CustomInput } from '@components/CustomInput'
import { SuccessIcon } from '@icons'
import { Button, ErrorDisplay, Input } from 'mmfintech-portal-commons'
import { ReportProblemStyled, ReportProblemSuccess, ReportProblemTitle } from './ReportProblem.styled'

import type { ReportProblemRequest } from 'mmfintech-checkout-commons/types'

export const ReportProblem = ({ visible, onClose }) => {
  const [succeeded, setSucceeded] = useState(false)

  const sessionId = useSessionId()
  const { reportProblemError, reportProblemFetching, reportProblemRequest, reset } = useProblemReporting()

  const formValues = useFormValues({
    problem: { required: true },
    description: { validation: 'safe-string' }
  })

  const updateRequired = (value: string): void => {
    formValues.setError('description', null)
    formValues.setRequired('description', value === 'Other')
  }

  const handleSubmit = () => {
    updateRequired(formValues.getValue('problem'))
    if (formValues.areValid()) {
      void reportProblemRequest(sessionId, formValues.prepare() as ReportProblemRequest, () => setSucceeded(true))
    }
  }

  const problems = () => [
    { value: 'Transaction Failure', label: tr('CHECKOUT.PROBLEM.TYPES.FAILURE', 'Transaction Failure') },
    { value: 'Interface Issue', label: tr('CHECKOUT.PROBLEM.TYPES.INTERFACE_ISSUE', 'Interface Issue') },
    { value: 'Information Discrepancy', label: tr('CHECKOUT.PROBLEM.TYPES.DISCREPANCY', 'Information Discrepancy') },
    { value: 'Slow Response', label: tr('CHECKOUT.PROBLEM.TYPES.SLOW_RESPONSE', 'Slow Response') },
    { value: 'Security Concern', label: tr('CHECKOUT.PROBLEM.TYPES.SECURITY', 'Security Concern') },
    {
      value: 'Login/Authentication Problem',
      label: tr('CHECKOUT.PROBLEM.TYPES.LOGIN', 'Login/Authentication Problem')
    },
    { value: 'Other', label: tr('CHECKOUT.PROBLEM.TYPES.OTHER', 'Other') }
  ]

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on modal hide
  useEffect(() => {
    if (!visible) {
      reset()
      setSucceeded(false)
    }
  }, [visible])

  return (
    <ReportProblemStyled>
      {succeeded ? (
        <>
          <ReportProblemSuccess>
            <SuccessIcon />
            <div>{tr('CHECKOUT.PROBLEM.SUCCESS', 'Your problem was successfully reported')}</div>
          </ReportProblemSuccess>

          <Button
            type='button'
            color='primary'
            text={tr('FRONTEND.BUTTONS.OK', 'OK')}
            onClick={onClose}
            data-test='button-submit'
          />
        </>
      ) : (
        <>
          <ReportProblemTitle>{tr('CHECKOUT.PROBLEM.TITLE', 'Report a Problem')}</ReportProblemTitle>

          {settings.blixcard ? (
            <CustomInput
              type='select'
              label={tr('CHECKOUT.PROBLEM.TYPE_LABEL', 'Problem')}
              {...formValues.registerInput('problem', updateRequired)}
              options={problems()}
              isSearchable={false}
              data-test='problem'
            />
          ) : (
            <Input
              type='select'
              placeholder={tr('CHECKOUT.PROBLEM.TYPE_PLACEHOLDER', 'Select')}
              label={tr('CHECKOUT.PROBLEM.TYPE_LABEL', 'Problem')}
              {...formValues.registerInput('problem', updateRequired)}
              options={problems()}
              isSearchable={false}
              data-test='problem'
            />
          )}

          {settings.blixcard ? (
            <CustomInput
              type='textarea'
              label={tr('CHECKOUT.PROBLEM.DESCRIPTION_LABEL', 'Describe the Problem')}
              {...formValues.registerInput('description')}
              data-test='description'
              rows={5}
            />
          ) : (
            <Input
              type='textarea'
              label={tr('CHECKOUT.PROBLEM.DESCRIPTION_LABEL', 'Describe the Problem')}
              {...formValues.registerInput('description')}
              data-test='description'
              rows={5}
            />
          )}

          <ErrorDisplay error={reportProblemError} />

          <Button
            type='button'
            color='primary'
            text={tr('CHECKOUT.PROBLEM.BUTTON_SUBMIT', 'Submit')}
            onClick={handleSubmit}
            loading={reportProblemFetching}
            data-test='button-submit'
          />
        </>
      )}
    </ReportProblemStyled>
  )
}
